/**
 * Equipment Catalog — E-Commerce Experience
 * Browse equipment, add to cart, checkout, view orders.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter
} from '../components/ui/sheet';
import { toast } from 'sonner';
import {
  Package, Search, Shield, Wifi, Tag, Box, ShoppingCart,
  Plus, Minus, Trash2, ChevronRight, ArrowLeft,
  CheckCircle, Clock, Truck, X, Filter, Grid3X3,
  List, MapPin, CreditCard, FileText, Eye
} from 'lucide-react';

const PLATFORMS = ['CLOVER', 'XEROCHARGE', 'SQUARE', 'EXATOUCH', 'INGENICO', 'PAYANYWHERE'];
const CATEGORIES = ['Terminal', 'POS', 'Kiosk', 'Printer', 'Accessories', 'Card Reader', 'Pin Pad'];

export default function EquipmentCatalog() {
  const { user } = useAuthStore();
  const isSA = user?.role === 'super_admin';

  // Data
  const [catalog, setCatalog] = useState([]);
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [category, setCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // Navigation
  const [activeView, setActiveView] = useState('catalog'); // catalog | cart | checkout | orders | order-detail
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Cart
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // Checkout
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [shippingAddress, setShippingAddress] = useState({
    name: '', address_line_1: '', address_line_2: '', city: '', state: '', zip: '', phone: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('invoice');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Product detail
  const [detailProduct, setDetailProduct] = useState(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (platform !== 'all') params.append('platform', platform);
      if (category !== 'all') params.append('category', category);
      const [catalogRes, statsRes] = await Promise.all([
        api.request(`/equipment/catalog?${params}`),
        api.request('/equipment/stats'),
      ]);
      setCatalog(catalogRes);
      setStats(statsRes);
    } catch (err) {
      console.error('Failed to load equipment:', err);
    }
    setLoading(false);
  }, [platform, category]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.request('/equipment/orders');
      setOrders(res);
    } catch (err) {
      console.error('Failed to load orders:', err);
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { if (activeView === 'orders') loadOrders(); }, [activeView, loadOrders]);

  // ── Cart Logic ──
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        const maxQty = product.quantity_in_stock || 1;
        if (existing.quantity >= maxQty) {
          toast.error(`Only ${maxQty} units available`);
          return prev;
        }
        return prev.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      toast.success(`${product.product_name} added to cart`);
      return [...prev, {
        product_id: product.id,
        product_name: product.product_name,
        platform: product.platform,
        category: product.category || 'Terminal',
        unit_price: product.sell_price,
        quantity: 1,
        max_quantity: product.quantity_in_stock || 1,
        image_url: product.image_url || '',
      }];
    });
  };

  const updateCartQty = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id !== productId) return item;
      const newQty = item.quantity + delta;
      if (newQty < 1) return item;
      if (newQty > item.max_quantity) {
        toast.error(`Only ${item.max_quantity} units available`);
        return item;
      }
      return { ...item, quantity: newQty };
    }));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(i => i.product_id !== productId));
  };

  const cartTotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  // ── Checkout Logic ──
  const handlePlaceOrder = async () => {
    setSubmitting(true);
    try {
      const orderData = {
        items: cart.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          platform: i.platform,
          category: i.category,
        })),
        shipping_address: shippingAddress,
        payment_method: paymentMethod,
        notes: orderNotes,
      };
      await api.request('/equipment/orders', {
        method: 'POST',
        body: JSON.stringify(orderData),
      });
      toast.success('Order placed successfully!');
      setCart([]);
      setCheckoutStep(0);
      setActiveView('orders');
      loadOrders();
      loadCatalog();
    } catch (err) {
      toast.error(err.message || 'Failed to place order');
    }
    setSubmitting(false);
  };

  // ── Filter catalog ──
  const filtered = catalog.filter(item => {
    const q = search.toLowerCase();
    return (item.product_name || '').toLowerCase().includes(q) ||
           (item.description || '').toLowerCase().includes(q) ||
           (item.platform || '').toLowerCase().includes(q);
  });

  const platformCounts = {};
  catalog.forEach(item => {
    platformCounts[item.platform] = (platformCounts[item.platform] || 0) + 1;
  });

  // ── Render ──
  return (
    <div className="space-y-6" data-testid="equipment-catalog">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-heading" data-testid="equipment-page-title">
            {activeView === 'catalog' && 'Equipment Store'}
            {activeView === 'cart' && 'Shopping Cart'}
            {activeView === 'checkout' && 'Checkout'}
            {activeView === 'orders' && 'My Orders'}
            {activeView === 'order-detail' && 'Order Details'}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {activeView === 'catalog' && 'Browse and purchase equipment for your merchants'}
            {activeView === 'cart' && `${cartCount} item${cartCount !== 1 ? 's' : ''} in your cart`}
            {activeView === 'checkout' && 'Complete your equipment order'}
            {activeView === 'orders' && `${orders.length} order${orders.length !== 1 ? 's' : ''} total`}
            {activeView === 'order-detail' && `Order #${selectedOrder?.id?.slice(-8).toUpperCase()}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeView !== 'catalog' && activeView !== 'orders' && (
            <Button
              variant="outline"
              onClick={() => setActiveView(activeView === 'order-detail' ? 'orders' : 'catalog')}
              data-testid="back-to-catalog-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          {(activeView === 'catalog' || activeView === 'orders') && (
            <div className="flex bg-muted rounded-lg p-1">
              <button
                onClick={() => setActiveView('catalog')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'catalog' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-catalog"
              >
                Store
              </button>
              <button
                onClick={() => setActiveView('orders')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'orders' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-orders"
              >
                Orders
              </button>
            </div>
          )}
          {activeView === 'catalog' && (
            <Button
              onClick={() => { cart.length > 0 ? setActiveView('cart') : setCartOpen(true); }}
              className="relative"
              variant={cart.length > 0 ? 'default' : 'outline'}
              data-testid="cart-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* ═══════════ CATALOG VIEW ═══════════ */}
      {activeView === 'catalog' && (
        <>
          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Products', value: stats.total_products || 0, icon: Package, color: 'text-primary' },
              { label: 'In Stock', value: stats.in_stock || 0, icon: CheckCircle, color: 'text-green-700' },
              { label: 'Low Stock', value: stats.low_stock_count || 0, icon: Clock, color: (stats.low_stock_count || 0) > 0 ? 'text-orange-600' : 'text-muted-foreground' },
              { label: 'Deployed', value: stats.deployed || 0, icon: Box, color: 'text-purple-700' },
            ].map(s => (
              <div key={s.label} className="bg-card border rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <s.icon className={`h-5 w-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                  <p className={`text-xl font-bold ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Platform Pills */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setPlatform('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                platform === 'all'
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
              }`}
              data-testid="platform-filter-all"
            >
              All ({catalog.length})
            </button>
            {PLATFORMS.map(p => (
              <button
                key={p}
                onClick={() => setPlatform(platform === p ? 'all' : p)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  platform === p
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                }`}
                data-testid={`platform-filter-${p.toLowerCase()}`}
              >
                {p} {platformCounts[p] ? `(${platformCounts[p]})` : ''}
              </button>
            ))}
          </div>

          {/* Search + Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search equipment..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="eq-search"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-44" data-testid="eq-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}
                data-testid="view-grid"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}
                data-testid="view-list"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-card border rounded-xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-8 bg-muted rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-card border rounded-xl">
              <Package className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No equipment found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your filters or search terms.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isSA={isSA}
                  onAddToCart={() => addToCart(product)}
                  onViewDetail={() => setDetailProduct(product)}
                  inCart={cart.some(i => i.product_id === product.id)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(product => (
                <ProductListItem
                  key={product.id}
                  product={product}
                  isSA={isSA}
                  onAddToCart={() => addToCart(product)}
                  onViewDetail={() => setDetailProduct(product)}
                  inCart={cart.some(i => i.product_id === product.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══════════ CART VIEW ═══════════ */}
      {activeView === 'cart' && (
        <CartView
          cart={cart}
          onUpdateQty={updateCartQty}
          onRemove={removeFromCart}
          cartTotal={cartTotal}
          onCheckout={() => { setActiveView('checkout'); setCheckoutStep(0); }}
          onContinueShopping={() => setActiveView('catalog')}
        />
      )}

      {/* ═══════════ CHECKOUT VIEW ═══════════ */}
      {activeView === 'checkout' && (
        <CheckoutView
          cart={cart}
          cartTotal={cartTotal}
          step={checkoutStep}
          setStep={setCheckoutStep}
          shippingAddress={shippingAddress}
          setShippingAddress={setShippingAddress}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          orderNotes={orderNotes}
          setOrderNotes={setOrderNotes}
          onPlaceOrder={handlePlaceOrder}
          submitting={submitting}
          onBack={() => setActiveView('cart')}
        />
      )}

      {/* ═══════════ ORDERS VIEW ═══════════ */}
      {activeView === 'orders' && (
        <OrdersView
          orders={orders}
          onViewOrder={(order) => { setSelectedOrder(order); setActiveView('order-detail'); }}
        />
      )}

      {/* ═══════════ ORDER DETAIL VIEW ═══════════ */}
      {activeView === 'order-detail' && selectedOrder && (
        <OrderDetailView order={selectedOrder} />
      )}

      {/* Product Detail Dialog */}
      <Dialog open={!!detailProduct} onOpenChange={(open) => { if (!open) setDetailProduct(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailProduct && (
            <ProductDetailModal
              product={detailProduct}
              isSA={isSA}
              onAddToCart={() => { addToCart(detailProduct); setDetailProduct(null); }}
              inCart={cart.some(i => i.product_id === detailProduct.id)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Sheet (mini cart when clicking cart icon from catalog) */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-[400px] sm:w-[440px] flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Cart ({cartCount})
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            ) : cart.map(item => (
              <div key={item.product_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <Package className="h-5 w-5 text-muted-foreground/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.product_name}</p>
                  <p className="text-xs text-muted-foreground">{item.platform}</p>
                  <p className="text-sm font-bold text-primary">${(item.unit_price * item.quantity).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => updateCartQty(item.product_id, -1)} className="p-1 hover:bg-muted rounded"><Minus className="h-3 w-3" /></button>
                  <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                  <button onClick={() => updateCartQty(item.product_id, 1)} className="p-1 hover:bg-muted rounded"><Plus className="h-3 w-3" /></button>
                </div>
                <button onClick={() => removeFromCart(item.product_id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {cart.length > 0 && (
            <SheetFooter className="border-t pt-4 flex-col gap-3">
              <div className="flex justify-between text-lg font-bold w-full">
                <span>Total</span>
                <span className="text-primary">${cartTotal.toFixed(2)}</span>
              </div>
              <Button
                className="w-full"
                onClick={() => { setCartOpen(false); setActiveView('cart'); }}
                data-testid="sheet-view-cart-btn"
              >
                View Cart <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Product Card Component
// ═══════════════════════════════════════════════════════════
function ProductCard({ product, isSA, onAddToCart, onViewDetail, inCart }) {
  const inStock = (product.quantity_in_stock || 0) > 0;
  const isLowStock = inStock && (product.quantity_in_stock || 0) < 20;
  return (
    <div
      className="bg-card border rounded-xl overflow-hidden group hover:shadow-lg transition-shadow duration-200"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div
        className="h-48 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center relative cursor-pointer overflow-hidden"
        onClick={onViewDetail}
      >
        {product.image_url ? (
          <img src={product.image_url} alt={product.product_name} className="h-full w-full object-contain p-6 group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <Package className="h-20 w-20 text-muted-foreground/15" />
        )}
        {/* Platform badge */}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] px-2 py-1 bg-primary/90 text-white rounded-full font-semibold tracking-wide">
            {product.platform}
          </span>
        </div>
        {/* Stock indicator */}
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
            !inStock ? 'bg-red-100 text-red-800' :
            isLowStock ? 'bg-orange-100 text-orange-800' :
            'bg-green-100 text-green-800'
          }`}>
            {!inStock ? 'Out of stock' : isLowStock ? `Low: ${product.quantity_in_stock} left` : `${product.quantity_in_stock} in stock`}
          </span>
        </div>
        {/* Quick View overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
            <Eye className="h-3 w-3 inline mr-1" /> Quick View
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors cursor-pointer" onClick={onViewDetail}>
            {product.product_name}
          </h3>
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{product.category || 'Terminal'}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              product.condition === 'new' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {product.condition || 'new'}
            </span>
          </div>
        </div>

        {product.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
        )}

        {/* Pricing + Action */}
        <div className="flex items-end justify-between pt-1">
          <div>
            {isSA && product.cost_price != null && (
              <p className="text-[10px] text-muted-foreground line-through">${product.cost_price?.toFixed(2)} cost</p>
            )}
            <p className="text-xl font-bold text-primary" data-testid={`price-${product.id}`}>
              ${product.sell_price?.toFixed(2)}
            </p>
          </div>
          <Button
            size="sm"
            onClick={onAddToCart}
            disabled={!inStock}
            variant={inCart ? 'secondary' : 'default'}
            data-testid={`add-to-cart-${product.id}`}
          >
            {inCart ? (
              <><CheckCircle className="h-3.5 w-3.5 mr-1" /> In Cart</>
            ) : (
              <><Plus className="h-3.5 w-3.5 mr-1" /> Add to Cart</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Product List Item Component
// ═══════════════════════════════════════════════════════════
function ProductListItem({ product, isSA, onAddToCart, onViewDetail, inCart }) {
  const inStock = (product.quantity_in_stock || 0) > 0;
  const isLowStock = inStock && (product.quantity_in_stock || 0) < 20;
  return (
    <div
      className="bg-card border rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
      data-testid={`product-list-${product.id}`}
    >
      <div
        className="w-20 h-20 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden"
        onClick={onViewDetail}
      >
        {product.image_url ? (
          <img src={product.image_url} alt="" className="w-full h-full object-contain p-2" />
        ) : (
          <Package className="h-8 w-8 text-muted-foreground/20" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm cursor-pointer hover:text-primary transition-colors" onClick={onViewDetail}>
            {product.product_name}
          </h3>
          <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">{product.platform}</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{product.category || 'Terminal'}</span>
        </div>
        {product.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{product.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1.5">
          {(product.processor_compatibility || []).slice(0, 3).map(p => (
            <span key={p} className="text-[10px] text-muted-foreground">{p}</span>
          ))}
        </div>
      </div>
      <div className="text-right flex-shrink-0 space-y-1">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
          !inStock ? 'bg-red-50 text-red-700' :
          isLowStock ? 'bg-orange-50 text-orange-700' :
          'bg-green-50 text-green-700'
        }`}>
          {!inStock ? 'Out of stock' : isLowStock ? `Low: ${product.quantity_in_stock}` : `${product.quantity_in_stock} in stock`}
        </span>
        <p className="text-xl font-bold text-primary">${product.sell_price?.toFixed(2)}</p>
      </div>
      <Button
        size="sm"
        onClick={onAddToCart}
        disabled={!inStock}
        variant={inCart ? 'secondary' : 'default'}
        className="flex-shrink-0"
        data-testid={`list-add-to-cart-${product.id}`}
      >
        {inCart ? <CheckCircle className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </Button>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Product Detail Modal
// ═══════════════════════════════════════════════════════════
function ProductDetailModal({ product, isSA, onAddToCart, inCart }) {
  const inStock = (product.quantity_in_stock || 0) > 0;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg">{product.product_name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-5">
        {/* Image */}
        <div className="h-56 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl flex items-center justify-center overflow-hidden">
          {product.image_url ? (
            <img src={product.image_url} alt={product.product_name} className="h-full w-full object-contain p-8" />
          ) : (
            <Package className="h-24 w-24 text-muted-foreground/15" />
          )}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="default">{product.platform}</Badge>
          <Badge variant="outline">{product.category || 'Terminal'}</Badge>
          <Badge variant={product.condition === 'new' ? 'default' : 'secondary'}>
            {product.condition || 'new'}
          </Badge>
          <Badge variant={inStock ? 'default' : 'destructive'} className={inStock ? 'bg-green-600' : ''}>
            {inStock ? `${product.quantity_in_stock} in stock` : 'Out of stock'}
          </Badge>
        </div>

        {product.description && (
          <p className="text-sm text-muted-foreground">{product.description}</p>
        )}

        {/* Pricing */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Price</p>
              <p className="text-3xl font-bold text-primary">${product.sell_price?.toFixed(2)}</p>
              {isSA && product.cost_price != null && (
                <p className="text-xs text-muted-foreground mt-1">Cost: ${product.cost_price?.toFixed(2)} | Margin: ${(product.sell_price - product.cost_price).toFixed(2)}</p>
              )}
            </div>
            <Button size="lg" onClick={onAddToCart} disabled={!inStock || inCart} data-testid="detail-add-to-cart">
              {inCart ? (
                <><CheckCircle className="h-4 w-4 mr-2" /> Added to Cart</>
              ) : (
                <><ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart</>
              )}
            </Button>
          </div>
        </div>

        {/* Specs */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Technical Specifications</h4>
          <div className="grid grid-cols-2 gap-3">
            {(product.processor_compatibility || []).length > 0 && (
              <div className="bg-muted/20 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Processors</p>
                <div className="flex flex-wrap gap-1">
                  {product.processor_compatibility.map(p => (
                    <span key={p} className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">{p}</span>
                  ))}
                </div>
              </div>
            )}
            {product.encryption_requirements && (
              <div className="bg-muted/20 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Encryption</p>
                <p className="text-sm font-medium flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-primary" /> {product.encryption_requirements}</p>
              </div>
            )}
            {product.sim_wifi_capability && product.sim_wifi_capability !== 'None' && (
              <div className="bg-muted/20 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Connectivity</p>
                <p className="text-sm font-medium flex items-center gap-1.5"><Wifi className="h-3.5 w-3.5 text-primary" /> {product.sim_wifi_capability}</p>
              </div>
            )}
            {product.key_injection_status && product.key_injection_status !== 'not_required' && (
              <div className="bg-muted/20 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Key Injection</p>
                <p className="text-sm font-medium flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-primary" /> {product.key_injection_status.replace(/_/g, ' ')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════
// Cart View
// ═══════════════════════════════════════════════════════════
function CartView({ cart, onUpdateQty, onRemove, cartTotal, onCheckout, onContinueShopping }) {
  if (cart.length === 0) {
    return (
      <div className="text-center py-20 bg-card border rounded-xl">
        <ShoppingCart className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
        <p className="text-sm text-muted-foreground mb-6">Browse our equipment catalog to find what you need.</p>
        <Button onClick={onContinueShopping} data-testid="continue-shopping-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Continue Shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Cart Items */}
      <div className="lg:col-span-2 space-y-3">
        {cart.map(item => (
          <div key={item.product_id} className="bg-card border rounded-xl p-4 flex items-center gap-4" data-testid={`cart-item-${item.product_id}`}>
            <div className="w-20 h-20 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              {item.image_url ? (
                <img src={item.image_url} alt="" className="w-full h-full object-contain p-2" />
              ) : (
                <Package className="h-8 w-8 text-muted-foreground/20" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">{item.product_name}</h3>
              <div className="flex gap-2 mt-1">
                <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">{item.platform}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{item.category}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">${item.unit_price.toFixed(2)} each</p>
            </div>
            {/* Quantity */}
            <div className="flex items-center border rounded-lg">
              <button
                onClick={() => onUpdateQty(item.product_id, -1)}
                className="px-3 py-2 hover:bg-muted transition-colors rounded-l-lg"
                data-testid={`cart-minus-${item.product_id}`}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="px-4 py-2 text-sm font-medium border-x min-w-[40px] text-center" data-testid={`cart-qty-${item.product_id}`}>
                {item.quantity}
              </span>
              <button
                onClick={() => onUpdateQty(item.product_id, 1)}
                className="px-3 py-2 hover:bg-muted transition-colors rounded-r-lg"
                data-testid={`cart-plus-${item.product_id}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Line Total */}
            <div className="text-right flex-shrink-0 min-w-[80px]">
              <p className="text-lg font-bold text-primary">${(item.unit_price * item.quantity).toFixed(2)}</p>
            </div>
            {/* Remove */}
            <button
              onClick={() => onRemove(item.product_id)}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              data-testid={`cart-remove-${item.product_id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Order Summary Sidebar */}
      <div className="lg:col-span-1">
        <div className="bg-card border rounded-xl p-6 sticky top-24 space-y-4">
          <h3 className="font-semibold text-lg">Order Summary</h3>
          <Separator />
          <div className="space-y-2">
            {cart.map(item => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground truncate pr-2">{item.product_name} x{item.quantity}</span>
                <span className="font-medium flex-shrink-0">${(item.unit_price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <Separator />
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">${cartTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Shipping</span>
            <span className="font-medium text-green-700">Free</span>
          </div>
          <Separator />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-primary">${cartTotal.toFixed(2)}</span>
          </div>
          <Button className="w-full" size="lg" onClick={onCheckout} data-testid="proceed-checkout-btn">
            Proceed to Checkout <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
          <Button variant="outline" className="w-full" onClick={onContinueShopping}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Checkout View (Multi-Step)
// ═══════════════════════════════════════════════════════════
function CheckoutView({ cart, cartTotal, step, setStep, shippingAddress, setShippingAddress, paymentMethod, setPaymentMethod, orderNotes, setOrderNotes, onPlaceOrder, submitting, onBack }) {
  const steps = ['Shipping', 'Payment', 'Review'];
  const updateField = (field, value) => setShippingAddress(prev => ({ ...prev, [field]: value }));

  const canProceedShipping = shippingAddress.name && shippingAddress.address_line_1 && shippingAddress.city && shippingAddress.state && shippingAddress.zip;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Stepper */}
      <div className="flex items-center justify-center gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                i < step ? 'bg-green-600 text-white' :
                i === step ? 'bg-primary text-white' :
                'bg-muted text-muted-foreground'
              }`}>
                {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${i <= step ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-16 h-0.5 ${i < step ? 'bg-green-600' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Step 0: Shipping */}
          {step === 0 && (
            <div className="bg-card border rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Shipping Address</h3>
                  <p className="text-xs text-muted-foreground">Where should we deliver the equipment?</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Full Name / Business Name</Label>
                  <Input value={shippingAddress.name} onChange={e => updateField('name', e.target.value)} className="mt-1" placeholder="John's Merchant LLC" data-testid="shipping-name" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address Line 1</Label>
                  <Input value={shippingAddress.address_line_1} onChange={e => updateField('address_line_1', e.target.value)} className="mt-1" placeholder="123 Main St" data-testid="shipping-address1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address Line 2 (Optional)</Label>
                  <Input value={shippingAddress.address_line_2} onChange={e => updateField('address_line_2', e.target.value)} className="mt-1" placeholder="Suite 100" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={shippingAddress.city} onChange={e => updateField('city', e.target.value)} className="mt-1" placeholder="New York" data-testid="shipping-city" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>State</Label>
                    <Input value={shippingAddress.state} onChange={e => updateField('state', e.target.value)} className="mt-1" placeholder="NY" data-testid="shipping-state" />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input value={shippingAddress.zip} onChange={e => updateField('zip', e.target.value)} className="mt-1" placeholder="10001" data-testid="shipping-zip" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Phone</Label>
                  <Input value={shippingAddress.phone} onChange={e => updateField('phone', e.target.value)} className="mt-1" placeholder="(555) 123-4567" data-testid="shipping-phone" />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" /> Back to Cart</Button>
                <Button onClick={() => setStep(1)} disabled={!canProceedShipping} data-testid="checkout-next-btn">
                  Continue to Payment <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 1: Payment */}
          {step === 1 && (
            <div className="bg-card border rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Payment Method</h3>
                  <p className="text-xs text-muted-foreground">Select how you'd like to pay</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { id: 'invoice', label: 'Invoice / Net 30', desc: 'Pay within 30 days of delivery', icon: FileText },
                  { id: 'credit_card', label: 'Credit Card', desc: 'Pay now with Visa, Mastercard, or Amex', icon: CreditCard },
                  { id: 'ach', label: 'ACH Bank Transfer', desc: 'Direct bank transfer', icon: CreditCard },
                ].map(method => (
                  <label
                    key={method.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === method.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                    }`}
                    data-testid={`payment-${method.id}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMethod === method.id}
                      onChange={() => setPaymentMethod(method.id)}
                      className="accent-primary"
                    />
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <method.icon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{method.label}</p>
                      <p className="text-xs text-muted-foreground">{method.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div>
                <Label>Order Notes (Optional)</Label>
                <Input
                  value={orderNotes}
                  onChange={e => setOrderNotes(e.target.value)}
                  className="mt-1"
                  placeholder="Special instructions, delivery preferences..."
                  data-testid="order-notes"
                />
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
                <Button onClick={() => setStep(2)} data-testid="checkout-review-btn">
                  Review Order <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && (
            <div className="bg-card border rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Review Your Order</h3>
                  <p className="text-xs text-muted-foreground">Please confirm all details before placing</p>
                </div>
              </div>

              {/* Shipping Summary */}
              <div className="bg-muted/30 rounded-xl p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Shipping</h4>
                  <button onClick={() => setStep(0)} className="text-xs text-primary hover:underline">Edit</button>
                </div>
                <p className="text-sm">{shippingAddress.name}</p>
                <p className="text-xs text-muted-foreground">
                  {shippingAddress.address_line_1}{shippingAddress.address_line_2 ? `, ${shippingAddress.address_line_2}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</p>
                {shippingAddress.phone && <p className="text-xs text-muted-foreground">{shippingAddress.phone}</p>}
              </div>

              {/* Payment Summary */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Payment</h4>
                  <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline">Edit</button>
                </div>
                <p className="text-sm mt-1 capitalize">{paymentMethod.replace(/_/g, ' ')}</p>
              </div>

              {/* Items */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Items ({cart.length})</h4>
                {cart.map(item => (
                  <div key={item.product_id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-muted rounded flex items-center justify-center overflow-hidden">
                        {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-contain" /> : <Package className="h-4 w-4 text-muted-foreground/40" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity} x ${item.unit_price.toFixed(2)}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold">${(item.unit_price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
                <Button size="lg" onClick={onPlaceOrder} disabled={submitting} data-testid="place-order-btn">
                  {submitting ? (
                    <><span className="animate-spin mr-2">&#9696;</span> Placing Order...</>
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-2" /> Place Order - ${cartTotal.toFixed(2)}</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Side Summary */}
        <div className="lg:col-span-1">
          <div className="bg-card border rounded-xl p-5 sticky top-24 space-y-3">
            <h4 className="font-semibold text-sm">Order Summary</h4>
            <div className="space-y-2">
              {cart.map(item => (
                <div key={item.product_id} className="flex justify-between text-xs">
                  <span className="text-muted-foreground truncate pr-2">{item.product_name} x{item.quantity}</span>
                  <span className="font-medium flex-shrink-0">${(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator />
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Shipping</span>
              <span className="text-green-700">Free</span>
            </div>
            <Separator />
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="text-primary">${cartTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Orders View
// ═══════════════════════════════════════════════════════════
function OrdersView({ orders, onViewOrder }) {
  const statusStyles = {
    pending: 'bg-amber-50 text-amber-700',
    approved: 'bg-blue-50 text-blue-700',
    processing: 'bg-blue-50 text-blue-700',
    shipped: 'bg-purple-50 text-purple-700',
    delivered: 'bg-green-50 text-green-700',
    canceled: 'bg-red-50 text-red-700',
  };

  if (orders.length === 0) {
    return (
      <div className="text-center py-20 bg-card border rounded-xl">
        <FileText className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No orders yet</h3>
        <p className="text-sm text-muted-foreground">Your equipment orders will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map(order => (
        <div
          key={order.id}
          className="bg-card border rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onViewOrder(order)}
          data-testid={`order-${order.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">Order #{order.id?.slice(-8).toUpperCase()}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '} - {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-xs px-3 py-1 rounded-full font-medium capitalize ${statusStyles[order.status] || 'bg-muted text-muted-foreground'}`}>
                {order.status}
              </span>
              <p className="text-lg font-bold text-primary">${order.total?.toFixed(2)}</p>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Order Detail View
// ═══════════════════════════════════════════════════════════
function OrderDetailView({ order }) {
  const statusStyles = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-blue-50 text-blue-700 border-blue-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    shipped: 'bg-purple-50 text-purple-700 border-purple-200',
    delivered: 'bg-green-50 text-green-700 border-green-200',
    canceled: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Status Banner */}
      <div className={`rounded-xl p-5 border ${statusStyles[order.status] || 'bg-muted border-border'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {order.status === 'delivered' ? (
              <CheckCircle className="h-6 w-6" />
            ) : order.status === 'shipped' ? (
              <Truck className="h-6 w-6" />
            ) : (
              <Clock className="h-6 w-6" />
            )}
            <div>
              <p className="font-semibold capitalize">Order {order.status}</p>
              <p className="text-xs opacity-80">Placed on {new Date(order.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>
          <p className="text-2xl font-bold">${order.total?.toFixed(2)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Shipping Info */}
        {order.shipping_address && Object.keys(order.shipping_address).length > 0 && (
          <div className="bg-card border rounded-xl p-5 space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Shipping Address</h4>
            <p className="text-sm">{order.shipping_address.name}</p>
            <p className="text-xs text-muted-foreground">{order.shipping_address.address_line_1}</p>
            {order.shipping_address.address_line_2 && <p className="text-xs text-muted-foreground">{order.shipping_address.address_line_2}</p>}
            <p className="text-xs text-muted-foreground">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
          </div>
        )}

        {/* Payment Info */}
        <div className="bg-card border rounded-xl p-5 space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Payment</h4>
          <p className="text-sm capitalize">{(order.payment_method || 'invoice').replace(/_/g, ' ')}</p>
          {order.notes && (
            <>
              <h4 className="text-sm font-semibold mt-3">Notes</h4>
              <p className="text-xs text-muted-foreground">{order.notes}</p>
            </>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-muted/30">
          <h4 className="text-sm font-semibold">Order Items</h4>
        </div>
        <div className="divide-y">
          {(order.items || []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4">
              <div className="w-14 h-14 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-contain p-1" /> : <Package className="h-6 w-6 text-muted-foreground/30" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{item.product_name}</p>
                <div className="flex gap-2 mt-0.5">
                  <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full">{item.platform}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{item.category}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm">${item.unit_price?.toFixed(2)} x {item.quantity}</p>
                <p className="text-sm font-bold text-primary">${item.line_total?.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Totals */}
        <div className="border-t bg-muted/20 px-5 py-4 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span>${order.subtotal?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Shipping</span>
            <span className="text-green-700">Free</span>
          </div>
          <Separator className="my-2" />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-primary">${order.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
